import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from "@react-navigation/native-stack";
import {
  ListItem,
  AHSeparator,
  Box,
  AHText,
  AHTeamIconCircleRelay,
  AHIcon,
} from "arkh";
import React, { useCallback } from "react";

import { View, FlatList } from "react-native";
import { useLazyLoadQuery, graphql, useFragment } from "react-relay";
import { TopicsAdminQuery } from "./__generated__/TopicsAdminQuery.graphql";
import { TopicsAdminTopicListItem_storeTopic$key } from "./__generated__/TopicsAdminTopicListItem_storeTopic.graphql";
import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { useNavigation } from "@react-navigation/native";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";
import { TopicsAdminDetailScreen } from "./TopicsAdminDetail";

export type TopicAdminStackParamList = {
  TopicsAdminList: undefined;
  TopicAdminDetail: { topicId: string };
};

const TopicAdminStack = createNativeStackNavigator<TopicAdminStackParamList>();

export function TopicsAdmin() {
  const getOptions = useNavigationHeaderOptions();
  const intl_tokens = useIntlTokens();
  return (
    <TopicAdminStack.Navigator>
      <TopicAdminStack.Screen
        name="TopicsAdminList"
        component={TopicsAdminListScreen}
        options={(params) =>
          getOptions({
            type: "BACK",
            headerTitle: intl_tokens.Topics,
            onBack() {
              params.navigation.goBack();
            },
          })
        }
      />
      <TopicAdminStack.Screen
        name="TopicAdminDetail"
        component={TopicsAdminDetailScreen}
        options={(params) =>
          getOptions({
            type: "SAVE-INIT",
            title: "",
            onCancel() {
              params.navigation.goBack();
            },
          })
        }
      />
    </TopicAdminStack.Navigator>
  );
}

function TopicsAdminListScreen(
  props: NativeStackScreenProps<TopicAdminStackParamList, "TopicsAdminList">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <TopicAdminList fetchKey={fk} navigation={props.navigation} />}
    </LazyQueryNetworkBoundrary>
  );
}

function TopicAdminList(props: {
  fetchKey: string;
  navigation: NativeStackScreenProps<
    TopicAdminStackParamList,
    "TopicsAdminList"
  >["navigation"];
}) {
  const data = useLazyLoadQuery<TopicsAdminQuery>(
    graphql`
      query TopicsAdminQuery {
        currentStore {
          id
          topics {
            id
            isPrivate

            ...TopicsAdminTopicListItem_storeTopic
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const topics = data.currentStore?.topics ?? [];

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof topics)[number]; index: number }) => {
      return (
        <TopicListItem
          topic={item}
          onPress={() => {
            props.navigation.navigateDeprecated("TopicAdminDetail", {
              topicId: item.id,
            });
          }}
        />
      );
    },
    [props.navigation]
  );

  const navigation = useNavigation();
  const intl_tokens = useIntlTokens();

  useNavigationHeader({
    type: "BACK",
    onBack() {
      navigation.goBack();
    },
    headerTitle: intl_tokens.Topics,
  });
  return (
    <View style={{ flex: 1 }}>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        ListFooterComponent={AHSeparator}
        data={topics}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
      />
    </View>
  );
}

function TopicListItem(props: {
  topic: TopicsAdminTopicListItem_storeTopic$key;
  onPress: (id: string) => void;
}) {
  const topic = useFragment(
    graphql`
      fragment TopicsAdminTopicListItem_storeTopic on StoreTopic {
        id
        name
        isPrivate
        teams {
          id
          name
          ...AHTeamIconCircle
        }
      }
    `,
    props.topic
  );

  const topicTeams = topic.teams ?? [];
  return (
    <ListItem
      onPress={() => {
        props.onPress(topic.id);
      }}
      right={<ListItem.NavArrow />}
      left={
        topic.isPrivate ? (
          <AHIcon type="locked" size="small" color="text-secondary" />
        ) : null
      }
    >
      <ListItem.Title>{topic.name}</ListItem.Title>
      <Box
        spaceTop="xsmall"
        style={{ alignItems: "center", flexDirection: "row" }}
      >
        {topicTeams.length > 0 ? (
          <React.Fragment>
            {topicTeams.map((team) => (
              <Box
                key={team.id}
                style={{ alignItems: "center", flexDirection: "row" }}
                spaceRight="small"
              >
                <AHTeamIconCircleRelay team={team} size="small" />
                <AHText spaceLeft="xsmall">{team.name}</AHText>
              </Box>
            ))}
          </React.Fragment>
        ) : (
          <NoMapping isPrivate={topic.isPrivate ?? false} />
        )}
      </Box>
    </ListItem>
  );
}

function NoMapping(props: { isPrivate: boolean }) {
  const intl_tokens = useIntlTokens();
  if (props.isPrivate) {
    return (
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        <AHIcon
          spaceRight="xsmall"
          type="info"
          size="small"
          color="text-primary"
        />
        <AHText color="text-primary">
          <FormattedMessage
            defaultMessage="No routed teams"
            description="StoreTopicAdin TopicListItem No routed teams"
          />
        </AHText>
      </Box>
    );
  }

  return <AHText>{intl_tokens.AllTeams}</AHText>;
}
